
































import Vue from 'vue'
import { State } from 'vuex-class'
import { Component, Prop } from 'vue-property-decorator'
import OzIcon from 'library/v4/components/OzIcon.vue'
import roundedHexagon from 'images/rounded-hexagon.svg'

// The original SVG path has width 32px, height 37px
const ORIGINAL_WIDTH = 32
const ORIGINAL_HEIGHT = 37

/**
 * @displayName Rounded Hexagon Icon
 */
@Component({ components: { OzIcon } })
export default class RoundedHexagonIcon extends Vue {
  /**
   * Height of the hexagon in pixels
   */
  @Prop({ default: 48 }) height!: number
  /**
   * Colors for the hexagon
   * Monochrome renders white on light-mode and black on dark-mode
   * @values pink, green, orange, blue, yellow, white, black, monochrome
   */
  @Prop({ default: 'pink' }) backgroundColor!: string
  /**
   * Name of OzIcon inside
   */
  @Prop({ default: '' }) iconName!: string
  /**
   * Font of OzIcon inside
   */
  @Prop({ default: 'oricon' }) iconFont!: string
  /**
   * Size of OzIcon inside
   */
  @Prop({ default: 24 }) iconSize!: number
  /**
   * Content of text inside
   */
  @Prop({ default: '' }) text!: string
  /**
   * Text colors
   * @values primary, secondary, tertiary, disabled
   */
  @Prop({ default: 'primary' }) color!: string
  /**
   * Line-height in multiplier of the icon inside
   */
  @Prop({ default: 1 }) iconLineHeight!: number
  /**
   * Set to true and pass click event to make this a button
   */
  @Prop({ default: false }) isButton!: boolean
  /**
   * Determines if we should stop propagating the click event when the hexagon is used as a button
   * @default false
   */
  @Prop({ default: false }) shouldStopClickPropagation!: boolean

  @State isChrome!: boolean
  @State isMac!: boolean

  get scale(): number {
    return this.height / ORIGINAL_HEIGHT
  }

  get hexWidth(): number {
    return ORIGINAL_WIDTH * this.scale
  }

  get hexHeight(): number {
    return this.height
  }

  get bgClasses(): string[] {
    switch (this.backgroundColor) {
      case 'pink':
        return ['bg-padlet-pink']
      case 'green':
        return ['bg-park']
      case 'orange':
        return ['bg-tangerine']
      case 'blue':
        return ['bg-oceanic']
      case 'yellow':
        return ['bg-canary-500']
      case 'purple':
        return ['bg-grape-500']
      case 'white':
        return ['bg-light-ui-200']
      case 'black':
        return ['bg-dark-ui-200']
      case 'monochrome':
        return ['bg-light-ui-200', 'dark:bg-nightfall']
      case 'transparent':
        return ['bg-transparent']
      case 'loadingColors':
        return ['bg-grape-500', 'dark:bg-canary-500']
    }
    return ['bg-light-ui-200']
  }

  get textClasses(): string[] {
    const color = this.color
    switch (this.backgroundColor) {
      case 'pink':
      case 'green':
      case 'orange':
      case 'blue':
      case 'purple':
      case 'black':
        return [
          color == 'primary' ? 'text-white-100' : '',
          color == 'secondary' ? 'text-white-200' : '',
          color == 'tertiary' ? 'text-white-300' : '',
          color == 'disabled' ? 'text-white-400' : '',
        ]
      case 'yellow':
      case 'white':
        return [
          color == 'primary' ? 'text-highway-100' : '',
          color == 'secondary' ? 'text-highway-200' : '',
          color == 'tertiary' ? 'text-highway-300' : '',
          color == 'disabled' ? 'text-highway-400' : '',
        ]
      case 'monochrome':
      case 'transparent':
      case 'loadingColors':
        return [
          color == 'primary' ? 'text-highway-100 dark:text-white-100' : '',
          color == 'secondary' ? 'text-highway-200 dark:text-white-200' : '',
          color == 'tertiary' ? 'text-highway-300 dark:text-white-300' : '',
          color == 'disabled' ? 'text-highway-400 dark:text-white-400' : '',
          color == 'invertedPrimary' ? 'text-white-100 dark:text-highway-100' : '',
        ]
    }
    return []
  }

  get hoverColorClasses(): string[] {
    if (!this.isButton) {
      return []
    }

    switch (this.backgroundColor) {
      case 'pink':
        return [
          'hover-hover:hover:animate-pulse-bg-color',
          'pulse-bg-from-padlet-pink-500',
          'pulse-bg-to-padlet-pink-600',
          'dark:pulse-bg-from-padlet-pink-500',
          'dark:pulse-bg-to-padlet-pink-400',
        ]
      case 'green':
        return [
          'hover-hover:hover:animate-pulse-bg-color',
          'pulse-bg-from-park-500',
          'pulse-bg-to-park-600',
          'dark:pulse-bg-from-park-500',
          'dark:pulse-bg-to-park-400',
        ]
      case 'orange':
        return [
          'hover-hover:hover:animate-pulse-bg-color',
          'pulse-bg-from-tangerine-500',
          'pulse-bg-to-tangerine-600',
          'dark:pulse-bg-from-tangerine-500',
          'dark:pulse-bg-to-tangerine-400',
        ]
      case 'blue':
        return [
          'hover-hover:hover:animate-pulse-bg-color',
          'pulse-bg-from-oceanic-500',
          'pulse-bg-to-oceanic-600',
          'dark:pulse-bg-from-oceanic-500',
          'dark:pulse-bg-to-oceanic-400',
        ]
      case 'purple':
        return [
          'hover-hover:hover:animate-pulse-bg-color',
          'pulse-bg-from-grape-500',
          'pulse-bg-to-grape-600',
          'dark:pulse-bg-from-grape-500',
          'dark:pulse-bg-to-grape-400',
        ]
      default:
        return []
    }
  }

  get activeFocusColorClasses(): string[] {
    if (!this.isButton) {
      return []
    }

    switch (this.backgroundColor) {
      case 'pink':
        return [
          'hover-hover:active:animate-none',
          'hover-hover:focus:animate-none',

          // Active
          'active:bg-padlet-pink-700',
          'dark:active:bg-padlet-pink-400',

          // Focus
          'focus:bg-padlet-pink-700',
          'dark:focus:bg-padlet-pink-400',
        ]
      case 'green':
        return [
          'hover-hover:active:animate-none',
          'hover-hover:focus:animate-none',

          // Active
          'active:bg-park-700',
          'dark:active:bg-park-400',

          // Focus
          'focus:bg-park-700',
          'dark:focus:bg-park-400',
        ]
      case 'orange':
        return [
          'hover-hover:active:animate-none',
          'hover-hover:focus:animate-none',

          // Active
          'active:bg-tangerine-700',
          'dark:active:bg-tangerine-400',

          // Focus
          'focus:bg-tangerine-700',
          'dark:focus:bg-tangerine-400',
        ]
      case 'blue':
        return [
          'hover-hover:active:animate-none',
          'hover-hover:focus:animate-none',

          // Active
          'active:bg-oceanic-700',
          'dark:active:bg-oceanic-400',

          // Focus
          'focus:bg-oceanic-700',
          'dark:focus:bg-oceanic-400',
        ]
      case 'purple':
        return [
          'hover-hover:active:animate-none',
          'hover-hover:focus:animate-none',

          // Active
          'active:bg-grape-700',
          'dark:active:bg-grape-400',

          // Focus
          'focus:bg-grape-700',
          'dark:focus:bg-grape-400',
        ]
      default:
        return []
    }
  }

  get styles(): Record<string, string> {
    // 24 Jan 2022: Chrome on MacOS has a bug that it turns the hexagon into a square on hover
    // if there is a transition. We temporarily fix this by using clip-path instead of mask-image.
    const maskImageOrClipPath: Record<string, string> =
      this.isChrome && this.isMac
        ? {
            // The SVG element is declared in app/views/layouts/surface/doodle.html.erb
            // clip-path support for external link is crappy so we have to put the SVG
            // there so it's available to be referenced by its id from anywhere in the
            // padlet page.
            // TODO: remove this hack when we have a better solution or Chrome fixes
            // the bug.
            clipPath: 'url(#rounded-hexagon-svg-clip-path)',
          }
        : {
            maskImage: this.hexMask,
            maskSize: '100% 100%',
          }
    return {
      minWidth: `${this.hexWidth}px`,
      maxWidth: `${this.hexWidth}px`,
      minHeight: `${this.hexHeight}px`,
      maxHeight: `${this.hexHeight}px`,
      ...maskImageOrClipPath,
    }
  }

  get iconStyle(): Record<string, string> {
    return {
      maxWidth: `${this.iconSize}px`,
      width: `${this.iconSize}px`,
      height: `${this.iconSize}px`,
      fontSize: ` ${this.iconSize}px`,
      lineHeight: `${this.iconLineHeight}`,
    }
  }

  get hexMask(): string {
    return `url(${roundedHexagon})`
  }

  click(event: MouseEvent): void {
    if (this.isButton) {
      /**
       * Click event, only work if `isButton` is true
       * @property {MouseEvent} event click event
       */
      this.$emit('click', event)
      if (this.shouldStopClickPropagation) {
        event.stopPropagation()
      }
    }
  }
}
